import proj4 from 'proj4';

import { getCrsDef, normalizeCrsCode } from '@/crs/crsCatalog';

type Coordinate = number[];

function transformCoordinates(value: unknown, transform: (coordinate: Coordinate) => Coordinate): unknown {
  if (!Array.isArray(value)) return value;
  if (value.length >= 2 && value.every((entry) => typeof entry === 'number')) {
    return transform(value as Coordinate);
  }
  return value.map((entry) => transformCoordinates(entry, transform));
}

async function ensureProjection(code: string): Promise<string> {
  const normalized = normalizeCrsCode(code);
  if (!proj4.defs(normalized)) {
    const definition = await getCrsDef(normalized);
    if (!definition.proj4) throw new Error(`CRS ${normalized} has no proj4 definition`);
    proj4.defs(normalized, definition.proj4);
  }
  return normalized;
}

/** Reinterpret raw vector coordinates in sourceCrs and return GeoJSON in targetCrs. */
export async function transformGeoJsonCoordinates(
  collection: Record<string, unknown>,
  sourceCrs: string | null | undefined,
  targetCrs: string,
): Promise<Record<string, unknown>> {
  if (!sourceCrs || normalizeCrsCode(sourceCrs) === normalizeCrsCode(targetCrs)) return collection;

  const source = await ensureProjection(sourceCrs);
  const target = await ensureProjection(targetCrs);
  const transform = (coordinate: Coordinate): Coordinate => {
    const transformed = proj4(source, target, coordinate);
    return Array.isArray(transformed) ? transformed : coordinate;
  };

  const features = Array.isArray(collection.features) ? collection.features : [];
  return {
    ...collection,
    features: features.map((feature) => {
      if (!feature || typeof feature !== 'object') return feature;
      const record = feature as Record<string, unknown>;
      const geometry = record.geometry;
      if (!geometry || typeof geometry !== 'object') return feature;
      const geometryRecord = geometry as Record<string, unknown>;
      if (!('coordinates' in geometryRecord)) return feature;
      return {
        ...record,
        geometry: {
          ...geometryRecord,
          coordinates: transformCoordinates(geometryRecord.coordinates, transform),
        },
      };
    }),
  };
}